import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, ArrowUpDown, Search, Home, ChevronRight } from 'lucide-react';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import LoginPopup from '../components/LoginPopup';
import { useFavoriteAuth } from '../hooks/useFavoriteAuth';
import { getGroceryShopById, getGroceryShopProducts, getPublicGroceryDishes } from '../services/groceryShopApi';
import { loadFavorites } from '../utils/favoritesManager';
import { getGroceryCategoryIcon } from '../utils/groceryCategoryIcon';
import namkeFallback from '../assets/namke-fallback.png';

const PRIMARY = 'rgb(5, 70, 37)';

const formatPrice = (price) => {
  const n = typeof price === 'string' ? parseFloat(price) : Number(price);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} €`;
};

const ProductFallback = () => (
  <div className="w-full h-full flex items-center justify-center bg-namke-cream">
    <img src={namkeFallback} alt="" className="w-full h-full object-cover" />
  </div>
);

const ShopAvatar = ({ shop, size = 80 }) => {
  const [imgError, setImgError] = useState(false);
  if (shop?.image_url && !imgError) {
    return (
      <img
        src={shop.image_url}
        alt=""
        onError={() => setImgError(true)}
        className="w-full h-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = (shop?.name || '?').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
  return (
    <div
      className="w-full h-full flex items-center justify-center font-bold text-white text-2xl select-none"
      style={{ backgroundColor: PRIMARY }}
    >
      {initials}
    </div>
  );
};

const ProductImage = ({ src, alt }) => {
  const [error, setError] = useState(false);
  if (!src || error) return <ProductFallback />;
  return (
    <img
      src={src}
      alt={alt || ''}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      onError={() => setError(true)}
    />
  );
};

const GroceryShopDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [shopFavorited, setShopFavorited] = useState(false);
  const [favoriteProducts, setFavoriteProducts] = useState({});

  const {
    isAuthenticated,
    showLoginPopup,
    handleFavoriteAction,
    handleLoginSuccess,
    closeLoginPopup,
    checkFavoriteStatus,
  } = useFavoriteAuth();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const fetchShop = async () => {
      setLoading(true);
      setError(null);
      try {
        const shopRes = await getGroceryShopById(id);
        const [catalogRes, dishRes] = await Promise.all([
          getGroceryShopProducts({ grocery_shop_id: id, active_only: true, limit: 200 }),
          shopRes?.restaurant_id
            ? getPublicGroceryDishes({ restaurant_id: shopRes.restaurant_id, limit: 200 }).catch(() => [])
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          setShop(shopRes);
          const catalogProducts = (Array.isArray(catalogRes) ? catalogRes : []).map((p) => ({
            ...p,
            source: 'catalog',
          }));
          const dishProducts = (Array.isArray(dishRes) ? dishRes : []).map((d) => ({
            id: d.id,
            source: 'dish',
            name: d.name,
            description: d.description,
            image_url: d.dish_image_url,
            price: d.price,
            category: d.category_name,
            origin_country: null,
            origin_region: null,
            is_african: false,
            stock_quantity: null,
            unit: null,
          }));
          setProducts([...catalogProducts, ...dishProducts]);
        }
      } catch (err) {
        console.error('Erreur chargement grocery shop:', err);
        if (!cancelled) setError(t('groceryShopDetails.notFoundOrUnavailable'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchShop();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShopFavorited(false);
      setFavoriteProducts({});
      return;
    }
    (async () => {
      try {
        const favorites = await loadFavorites();
        const productMap = {};
        favorites.forEach((fav) => {
          if (fav.favorited_object_type === 'grocery_product') productMap[`catalog-${fav.favorited_object_id}`] = true;
          if (fav.favorited_object_type === 'dish') productMap[`dish-${fav.favorited_object_id}`] = true;
          if (fav.favorited_object_type === 'grocery_shop' && fav.favorited_object_id === Number(id)) setShopFavorited(true);
        });
        setFavoriteProducts(productMap);
      } catch {
        setFavoriteProducts({});
      }
    })();
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !shop) return;
    checkFavoriteStatus(Number(id), 'grocery_shop').then(setShopFavorited).catch(() => {});
  }, [isAuthenticated, id, shop, checkFavoriteStatus]);

  const SORT_OPTIONS = useMemo(() => ([
    { key: 'featured', label: t('groceryShopDetails.sort.featured') },
    { key: 'price_low', label: t('groceryShopDetails.sort.priceLow') },
    { key: 'price_high', label: t('groceryShopDetails.sort.priceHigh') },
    { key: 'name', label: t('groceryShopDetails.sort.name') },
  ]), [t]);

  const categoryLabel = (category) => {
    if (category === 'Tous') return t('groceryShopDetails.allCategories');
    if (category === 'Autres') return t('groceryShopDetails.otherCategory');
    return category;
  };

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => set.add(p.category?.trim() || 'Autres'));
    return ['Tous', ...Array.from(set).sort()];
  }, [products]);

  const filteredAndSorted = useMemo(() => {
    let list = products;
    if (selectedCategory && selectedCategory !== 'Tous') {
      list = list.filter((p) => (p.category?.trim() || 'Autres') === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((p) => (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (sortBy === 'price_low') return Number(a.price) - Number(b.price);
      if (sortBy === 'price_high') return Number(b.price) - Number(a.price);
      if (sortBy === 'name') return String(a.name || '').localeCompare(String(b.name || ''), 'fr');
      return 0;
    });
  }, [products, selectedCategory, searchQuery, sortBy]);

  const handleToggleShopFavorite = async () => {
    if (!shop) return;
    const action = shopFavorited ? 'remove' : 'add';
    const success = await handleFavoriteAction(action, Number(shop.id), 'grocery_shop');
    if (success) setShopFavorited(!shopFavorited);
  };

  const handleToggleProductFavorite = async (productId, source) => {
    const key = `${source === 'dish' ? 'dish' : 'catalog'}-${productId}`;
    const isFav = Boolean(favoriteProducts[key]);
    const action = isFav ? 'remove' : 'add';
    const success = await handleFavoriteAction(action, Number(productId), source === 'dish' ? 'dish' : 'grocery_product');
    if (success) setFavoriteProducts((prev) => ({ ...prev, [key]: !isFav }));
  };

  if (loading) {
    return (
      <>
        <NavBar />
        <div className="min-h-screen bg-namke-white pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 animate-pulse space-y-6">
            <div className="h-48 bg-namke-cream rounded-3xl" />
            <div className="h-6 bg-namke-cream rounded-full w-1/3" />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-64 bg-namke-cream rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !shop) {
    return (
      <>
        <NavBar />
        <div className="min-h-screen bg-namke-white pt-24 pb-12 px-4">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-namke-cream flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" fill="none" stroke={PRIMARY} strokeWidth={2} className="w-8 h-8"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
            </div>
            <p className="text-namke-black/70 mb-4">{error || t('groceryShopDetails.notFound')}</p>
            <button type="button" onClick={() => navigate('/')} className="font-semibold text-sm underline" style={{ color: PRIMARY }}>
              {t('groceryShopDetails.backToHome')}
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const sortedByLabel = SORT_OPTIONS.find((o) => o.key === sortBy)?.label || SORT_OPTIONS[0].label;

  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-namke-white pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Fil d'Ariane */}
          <nav className="flex items-center gap-2 text-sm text-namke-black/60 mb-6">
            <Link to="/" className="flex items-center gap-1 hover:text-namke-black transition-colors">
              <Home size={14} />
              {t('groceryShopDetails.breadcrumbHome')}
            </Link>
            <ChevronRight size={13} className="text-namke-black/30" />
            <span className="text-namke-black font-medium">{shop.name}</span>
          </nav>

          {/* En-tête magasin */}
          <div className="bg-white rounded-3xl overflow-hidden mb-8">
            {/* Bannière */}
            <div className="relative h-36 sm:h-48 overflow-hidden bg-namke-cream">
              {shop.banner_url || shop.image_url ? (
                <img
                  src={shop.banner_url || shop.image_url}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <img src={namkeFallback} alt="" className="w-full h-full object-cover" />
              )}
            </div>

            {/* Infos */}
            <div className="px-6 pb-5 -mt-8 relative">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                {/* Avatar magasin */}
                <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-white flex-shrink-0">
                  <ShopAvatar shop={shop} size={64} />
                </div>

                <div className="flex-1 min-w-0 pt-2 sm:pt-0">
                  <h1 className="text-2xl font-bold text-namke-black leading-tight" style={{ fontFamily: 'Fraunces, Georgia, serif', letterSpacing: '-0.01em' }}>{shop.name}</h1>
                  <div className="flex flex-wrap items-center gap-3 mt-1">
                    {shop.city && (
                      <span className="text-sm text-namke-black/55 flex items-center gap-1">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-3.5 h-3.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
                        {shop.city}
                      </span>
                    )}
                    <span className="text-sm text-namke-black/45">{t('groceryShopDetails.productCount', { count: products.length })}</span>
                    {shop.restaurant_name && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: 'rgba(5, 70, 37, 0.08)', color: PRIMARY }}>
                        {shop.restaurant_name}
                      </span>
                    )}
                  </div>
                  {shop.description && (
                    <p className="text-sm text-namke-black/65 mt-2 line-clamp-2">{shop.description}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleToggleShopFavorite}
                  className="flex-shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition text-white"
                  style={{ backgroundColor: shopFavorited ? PRIMARY : 'rgb(239, 76, 35)' }}
                >
                  <Heart size={16} className={shopFavorited ? 'fill-white' : ''} />
                  {shopFavorited ? t('groceryShopDetails.favorited') : t('groceryShopDetails.addFavorite')}
                </button>
              </div>
            </div>
          </div>

          {/* Corps — sidebar + grille */}
          <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6">

            {/* Sidebar */}
            <aside className="space-y-4">
              {/* Recherche */}
              <div style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', padding: '16px' }}>
                <div className="relative">
                  <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-namke-black/40" />
                  <input
                    type="text"
                    placeholder={t('groceryShopDetails.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm focus:outline-none"
                    style={{ fontFamily: 'Poppins, sans-serif', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', color: '#051004' }}
                  />
                </div>
              </div>

              {/* Catégories */}
              <div style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', padding: '16px' }}>
                <h2 style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '12px' }}>{t('groceryShopDetails.categoriesTitle')}</h2>
                <div
                  className="flex flex-row lg:flex-col gap-2 lg:gap-1 overflow-x-auto lg:overflow-visible snap-x snap-proximity lg:snap-none scroll-smooth pb-1 lg:pb-0 [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {categories.map((category) => {
                    const isActive = selectedCategory === category;
                    return (
                      <button
                        key={category}
                        type="button"
                        onClick={() => setSelectedCategory(category)}
                        className="flex-shrink-0 snap-start lg:w-full flex items-center gap-3 text-left whitespace-nowrap lg:whitespace-normal transition-colors"
                        style={{
                          borderRadius: '999px',
                          padding: '10px 12px',
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '14px',
                          fontWeight: isActive ? 600 : 500,
                          background: isActive ? 'rgba(5,70,37,0.1)' : 'transparent',
                          color: isActive ? PRIMARY : 'rgba(5,16,4,0.7)',
                        }}
                      >
                        <img src={getGroceryCategoryIcon(category)} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        <span>{categoryLabel(category)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>

            {/* Grille produits */}
            <main>
              {/* Barre de tri */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-namke-black/55">
                  {t('groceryShopDetails.productCount', { count: filteredAndSorted.length })}
                  {selectedCategory !== 'Tous' ? ` · ${categoryLabel(selectedCategory)}` : ''}
                </span>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="flex items-center gap-2 text-sm transition-colors"
                    style={{ padding: '10px 18px', borderRadius: '999px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.14)', fontFamily: 'Archivo, sans-serif', fontWeight: 600 }}
                  >
                    <ArrowUpDown size={13} className="text-namke-black/40" />
                    {sortedByLabel}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5 text-namke-black/40"><path d="M6 9l6 6 6-6" /></svg>
                  </button>
                  {showSortDropdown && (
                    <div style={{ position: 'absolute', right: 0, marginTop: '8px', width: '190px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '18px', boxShadow: '0 14px 32px rgba(5,16,4,0.12)', overflow: 'hidden', zIndex: 10 }}>
                      {SORT_OPTIONS.map((o) => (
                        <button
                          key={o.key}
                          type="button"
                          onClick={() => { setSortBy(o.key); setShowSortDropdown(false); }}
                          className="w-full text-left transition-colors"
                          style={{ padding: '11px 16px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', background: sortBy === o.key ? '#FBF9D9' : 'transparent', color: sortBy === o.key ? PRIMARY : 'rgba(5,16,4,0.75)', fontWeight: sortBy === o.key ? 600 : 400 }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {filteredAndSorted.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center">
                  <div className="w-14 h-14 rounded-full bg-namke-cream flex items-center justify-center mx-auto mb-4">
                    <svg viewBox="0 0 64 64" fill="none" stroke={PRIMARY} strokeWidth="1.8" className="w-8 h-8"><rect x="10" y="22" width="44" height="32" rx="4" /><path d="M22 22v-4a10 10 0 0 1 20 0v4" /></svg>
                  </div>
                  <p className="text-namke-black/55 text-sm">{t('groceryShopDetails.noProductsForSelection')}</p>
                  {(selectedCategory !== 'Tous' || searchQuery) && (
                    <button type="button" className="mt-3 text-sm underline" style={{ color: PRIMARY }} onClick={() => { setSelectedCategory('Tous'); setSearchQuery(''); }}>
                      {t('groceryShopDetails.viewAllProducts')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredAndSorted.map((product) => {
                    const origin = [product.origin_country, product.origin_region].filter(Boolean).join(' · ');
                    const isDish = product.source === 'dish';
                    const detailHref = isDish ? `/product/${product.id}` : `/grocery-shop-product/${product.id}`;
                    const isProductFavorited = Boolean(favoriteProducts[`${isDish ? 'dish' : 'catalog'}-${product.id}`]);
                    return (
                      <article key={`${product.source || 'catalog'}-${product.id}`} className="group bg-white overflow-hidden transition-shadow hover:shadow-md flex flex-col" style={{ borderRadius: '22px', border: '1px solid rgba(5,16,4,0.08)' }}>
                        {/* Image */}
                        <Link to={detailHref} className="block relative h-48 overflow-hidden bg-namke-cream flex-shrink-0">
                          <ProductImage src={product.image_url} alt={product.name} />
                          {product.is_african && (
                            <span className="absolute top-2 left-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(5, 70, 37, 0.08)', color: PRIMARY }}>
                              {t('groceryShopDetails.africaBadge')}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleToggleProductFavorite(product.id, product.source); }}
                            className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow transition-colors ${isProductFavorited ? 'bg-red-500 text-white' : 'bg-white/90 text-namke-black/40 hover:text-red-500'}`}
                          >
                            <Heart size={14} className={isProductFavorited ? 'fill-white' : ''} />
                          </button>
                        </Link>

                        {/* Contenu */}
                        <div className="p-4 flex flex-col flex-1 gap-2">
                          <div>
                            <p className="text-xs text-namke-black/45 mb-0.5">{product.category ? categoryLabel(product.category) : categoryLabel('Autres')}</p>
                            <h3 className="text-sm font-semibold text-namke-black line-clamp-2 leading-snug" style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '15.5px' }}>{product.name}</h3>
                          </div>
                          {product.description && (
                            <p className="text-xs text-namke-black/55 line-clamp-2 leading-relaxed">{product.description}</p>
                          )}
                          {origin && <p className="text-xs text-namke-black/45">{t('groceryShopDetails.origin', { origin })}</p>}
                          {product.stock_quantity != null && product.stock_quantity <= 0 ? (
                            <p className="text-xs text-namke-terracotta font-semibold">{t('groceryShopDetails.outOfStock')}</p>
                          ) : product.stock_quantity != null && product.stock_quantity <= 5 && (
                            <p className="text-xs text-amber-600 font-medium">{t('groceryShopDetails.lowStock', { count: product.stock_quantity })}</p>
                          )}
                          <div className="flex items-center justify-between mt-auto pt-2 border-t border-namke-black/5">
                            <div>
                              <p className="text-base font-bold text-namke-black">{formatPrice(product.price)}</p>
                              {product.unit && <p className="text-xs text-namke-black/45">{product.unit}</p>}
                            </div>
                            <Link
                              to={detailHref}
                              className="text-xs font-semibold px-3.5 py-1.5 rounded-full text-white transition-opacity hover:opacity-90"
                              style={{ backgroundColor: PRIMARY }}
                            >
                              {t('groceryShopDetails.viewProduct')}
                            </Link>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>

      <Footer />

      {showLoginPopup && (
        <LoginPopup
          isOpen={showLoginPopup}
          onClose={closeLoginPopup}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
    </>
  );
};

export default GroceryShopDetails;
